import { CurrencyPipe, NgClass } from "@angular/common";
import { Component, EventEmitter, Input, LOCALE_ID, Output, inject } from "@angular/core";
import { ProductData } from "@domain/project/product-data/product-data";
import { ProductListDiscountKey } from "@domain/project/product-list/product-list-discount-key.util";
import {
  calculateDiscountedTotal,
  formatProductListDiscount,
  getProductListPriceData,
  isProductListDiscountEditable,
  normalizeProductListDiscountDisplay,
} from "@domain/project/product-list/product-list-discount.util";
import { TooltipDirective } from "@odx/angular/components/tooltip";

@Component({
  selector: "app-product-table-attachment-instructions",
  imports: [CurrencyPipe, TooltipDirective, NgClass],
  templateUrl: "./product-table-attachment-instructions.component.html",
  styleUrls: ["./product-table-attachment-instructions.component.scss"],
  standalone: true,
})
export class ProductTableAttachmentInstructionsComponent {
  private readonly locale = inject(LOCALE_ID);

  @Input() attachmentInstructions!: ProductData[];
  @Input() showCosts!: boolean;
  @Input() showZrefPrices = false;
  @Input() canEditDiscounts = false;

  @Output() discountChange = new EventEmitter<{ key: ProductListDiscountKey; value: string }>();

  protected readonly tooltipTextWarningSign: string = $localize`:@@productlist.table.warningSign.tooltip:Produkt enthält abgekündigte Komponenten oder ist Teil einer unvollständigen Konfiguration`;

  protected hasEditablePrice(product: ProductData): boolean {
    return isProductListDiscountEditable(
      this.canEditDiscounts,
      this.showCosts,
      getProductListPriceData(product, this.showZrefPrices),
      !!product.discountKey,
    );
  }

  protected hasPrice(product: ProductData): boolean {
    return !!getProductListPriceData(product, this.showZrefPrices);
  }

  protected getUnitPrice(product: ProductData): number | undefined {
    return getProductListPriceData(product, this.showZrefPrices)?.price;
  }

  protected getTotalPrice(product: ProductData): number | undefined {
    const unitPrice = this.getUnitPrice(product);
    return unitPrice === undefined ? undefined : calculateDiscountedTotal(unitPrice, product.quantity, product.discount ?? 0);
  }

  protected formatDiscount(discount: number): string {
    return formatProductListDiscount(discount, this.locale);
  }

  protected normalizeDiscountInput(input: HTMLInputElement, product: ProductData): void {
    const normalizedDiscount = normalizeProductListDiscountDisplay(input.value, product.discount ?? 0, this.locale);
    input.value = normalizedDiscount.formattedValue;
    if (normalizedDiscount.discount !== undefined && product.discountKey) {
      this.discountChange.emit({ key: product.discountKey, value: String(normalizedDiscount.discount) });
    }
  }
}
